import type { Client, Property, Quote, QuoteEntry, QuoteRun } from "./api.ts";

/**
 * What the quote and client pages show of the data, worked out once: a client's name, an address
 * on one line, a quote's best premium and what it saves against the client's current policy
 * (the property's `priorPremium`, read from their loan: docs/carrier-runs.md#preliminary-and-final-quotes).
 */

export const quoteLink = (quoteId: string) => `#/quotes/${encodeURIComponent(quoteId)}`;
export const clientLink = (clientId: string) => `#/clients/${encodeURIComponent(clientId)}`;

/** A business by its name, a person by first and last name, else by the contact's name. */
export function clientName(client: Client): string {
  const person = [client.firstName, client.lastName].filter(Boolean).join(" ");
  return client.businessName || person || client.contactName || "Unnamed client";
}

/** `1 Main St, Kansas City, MO 64111`, or an empty string without an address. */
export function addressLine(property: Property): string {
  const address = property.address;
  if (!address) return "";
  const street = [address.street, address.unit].filter(Boolean).join(" ");
  const region = [address.state, address.zip].filter(Boolean).join(" ");
  return [street, address.city, region].filter(Boolean).join(", ");
}

/** A quote without a stage is final. */
export function stageOf(quote: Quote): "preliminary" | "final" {
  return quote.stage ?? "final";
}

/** The quoted run with the lowest premium; none while no carrier gave one. */
export function bestRun(runs: readonly QuoteRun[]): QuoteRun | undefined {
  let best: QuoteRun | undefined;
  for (const run of runs) {
    if (run.status !== "quoted" || run.premium === undefined) continue;
    if (!best || run.premium < (best.premium as number)) best = run;
  }
  return best;
}

/** What the client pays now: the premium of the first covered property that has one. */
export function currentPolicy(
  properties: readonly Property[],
): { carrier?: string; premium: number; expires?: string } | undefined {
  const property = properties.find((candidate) => candidate.priorPremium !== undefined);
  if (!property) return undefined;
  return {
    premium: property.priorPremium as number,
    ...(property.priorCarrier && { carrier: property.priorCarrier }),
    ...(property.priorPolicyExpiration && { expires: property.priorPolicyExpiration }),
  };
}

export interface Savings {
  /** A year's premium saved; negative when the new one costs more. */
  readonly amount: number;
  /** Of the current premium, rounded to a whole percent. */
  readonly percent: number;
}

export function savingsOf(current: number | undefined, premium: number | undefined) {
  if (current === undefined || premium === undefined || current <= 0) return undefined;
  const amount = current - premium;
  return { amount, percent: Math.round((amount / current) * 100) } satisfies Savings;
}

/** A quote's best premium and what it saves, for the list and the quote's page. */
export function bestOffer(entry: QuoteEntry) {
  const run = bestRun(entry.runs);
  const current = currentPolicy(entry.properties);
  return { run, current, savings: savingsOf(current?.premium, run?.premium) };
}
